<template>
  <section class="referral-admin">
    <form class="admin-filter" @submit.prevent="loadRecords">
      <input
        v-model="referrerEmail"
        type="email"
        :placeholder="$t('referral.admin.filterPlaceholder')"
        :aria-label="$t('referral.admin.filterPlaceholder')"
      >
      <button type="submit" class="filter-btn" :disabled="isLoading">{{ $t('referral.admin.search') }}</button>
      <button v-if="referrerEmail" type="button" class="filter-btn" @click="clearFilter">{{ $t('referral.admin.clear') }}</button>
      <span class="record-count">{{ $t('referral.admin.total', { count: records.length }) }}</span>
    </form>

    <p v-if="loadError" class="load-error">{{ loadError }}</p>

    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>{{ $t('referral.admin.referrer') }}</th>
            <th>{{ $t('referral.admin.invitee') }}</th>
            <th>{{ $t('referral.admin.status') }}</th>
            <th>{{ $t('referral.admin.used') }}</th>
            <th>{{ $t('referral.admin.boundAt') }}</th>
            <th>{{ $t('referral.admin.rewards') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!records.length && !isLoading">
            <td colspan="6" class="empty-cell">{{ $t('referral.admin.empty') }}</td>
          </tr>
          <tr v-for="record in records" :key="record.inviteeUid">
            <td>
              <button type="button" class="email-link" @click="filterBy(record.referrerEmail)">{{ record.referrerEmail }}</button>
              <span class="mono muted">{{ record.code }}</span>
            </td>
            <td>
              {{ record.inviteeEmail }}
              <span class="mono muted">{{ record.inviteeUid }}</span>
            </td>
            <td>{{ statusLabel(record.status) }}</td>
            <td class="mono">{{ record.creditsUsed }}</td>
            <td class="mono">
              {{ formatTime(record.boundAt) }}
              <span v-if="record.activatedAt" class="muted">{{ $t('referral.statusActivated') }} {{ formatTime(record.activatedAt) }}</span>
              <span v-if="record.convertedAt" class="muted">{{ $t('referral.statusConverted') }} {{ formatTime(record.convertedAt) }}</span>
            </td>
            <td class="mono">
              <span>{{ $t('referral.admin.inviteeReward') }} +{{ record.inviteeReward }}</span>
              <span>
                {{ $t('referral.admin.referrerReward') }} +{{ record.referrerActivationReward }}
                <template v-if="record.referrerActivationCapped">({{ $t('referral.admin.cappedShort') }})</template>
              </span>
              <span>{{ $t('referral.admin.conversionReward') }} +{{ record.referrerConversionReward }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { apiPost } from '@/composables/useApi'

export default {
  name: 'ReferralAdminPanel',
  props: {
    adminPassword: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      records: [],
      referrerEmail: '',
      isLoading: false,
      loadError: ''
    }
  },
  mounted() {
    this.loadRecords()
  },
  methods: {
    async loadRecords() {
      this.isLoading = true
      this.loadError = ''
      try {
        const data = await apiPost('/api/admin/referrals', {
          adminPassword: this.adminPassword,
          referrerEmail: this.referrerEmail.trim() || null
        }, { auth: false })
        this.records = data.records
      } catch (error) {
        this.loadError = error.message
      } finally {
        this.isLoading = false
      }
    },
    filterBy(email) {
      this.referrerEmail = email
      this.loadRecords()
    },
    clearFilter() {
      this.referrerEmail = ''
      this.loadRecords()
    },
    statusLabel(status) {
      return {
        bound: this.$t('referral.statusBound'),
        activated: this.$t('referral.statusActivated'),
        converted: this.$t('referral.statusConverted')
      }[status]
    },
    formatTime(value) {
      return new Date(value).toLocaleString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style scoped>
.referral-admin {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.admin-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.admin-filter input {
  width: min(320px, 100%);
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
}

.admin-filter input:focus-visible {
  outline: none;
  border-color: var(--workspace-accent-dark);
  box-shadow: var(--focus-ring-brand);
}

.filter-btn {
  height: 36px;
  padding: 0 var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.filter-btn:hover:not(:disabled) {
  background: var(--surface-subtle);
}

.filter-btn:disabled {
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.filter-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.record-count {
  margin-left: auto;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.load-error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

th,
td {
  padding: var(--space-3);
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
  vertical-align: top;
}

th {
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  font-weight: 600;
  white-space: nowrap;
}

tbody tr:last-child td {
  border-bottom: 0;
}

td span {
  display: block;
}

.mono {
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.muted {
  color: var(--ink-tertiary);
}

.email-link {
  padding: 0;
  border: 0;
  background: none;
  color: var(--workspace-accent-dark);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.email-link:hover {
  text-decoration: underline;
}

.email-link:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.empty-cell {
  padding: var(--space-8);
  color: var(--ink-secondary);
  text-align: center;
}
</style>
